export const FULFILLMENT_TYPES = [
  "credentials",
  "invite",
  "code",
  "giftcard",
  "panel",
  "device",
  "recharge",
  "mailbox",
  "license",
];

export const SLOT_KINDS = ["none", "pin_profile", "email_member", "device", "site_login"];

export const PRODUCT_PRESETS = [
  { id: "giftcard", fulfillmentType: "giftcard", slotKind: "none", maxProfiles: 1, lotSize: 1, sellByProfile: false, sellFullAccount: true, loginPanel: false },
  { id: "streaming", fulfillmentType: "credentials", slotKind: "pin_profile", maxProfiles: 5, lotSize: 1, sellByProfile: true, sellFullAccount: true, loginPanel: false },
  { id: "fullAccount", fulfillmentType: "credentials", slotKind: "none", maxProfiles: 1, lotSize: 1, sellByProfile: false, sellFullAccount: true, loginPanel: false },
  { id: "iptv", fulfillmentType: "credentials", slotKind: "none", maxProfiles: 1, lotSize: 1, sellByProfile: false, sellFullAccount: true, loginPanel: true },
  // Hosting madre: cada puesto = URL + usuario + contraseña del sitio del cliente.
  { id: "hostingSites", fulfillmentType: "credentials", slotKind: "site_login", maxProfiles: 10, lotSize: 1, sellByProfile: true, sellFullAccount: false, loginPanel: false },
  { id: "family", fulfillmentType: "invite", slotKind: "email_member", maxProfiles: 5, lotSize: 1, loginPanel: false },
  { id: "canvaEdu", fulfillmentType: "invite", slotKind: "email_member", maxProfiles: 2, lotSize: 1, loginPanel: false },
  { id: "capcut", fulfillmentType: "device", slotKind: "device", maxProfiles: 3, deviceLimit: 3, lotSize: 1, loginPanel: false },
  { id: "code", fulfillmentType: "code", slotKind: "none", maxProfiles: 1, lotSize: 1, loginPanel: false },
  { id: "panel", fulfillmentType: "panel", slotKind: "none", maxProfiles: 10, lotSize: 1, loginPanel: false },
  { id: "recharge", fulfillmentType: "recharge", slotKind: "none", maxProfiles: 1, lotSize: 1, loginPanel: false },
  { id: "mailbox", fulfillmentType: "mailbox", slotKind: "none", maxProfiles: 1, lotSize: 1, loginPanel: false },
];

/** Preset de hosting madre / puestos con URL+usuario+clave. */
export const isHostingSitesPreset = (presetId) =>
  presetId === "hostingSites" || presetId === "sistemaCodigos";

/** IPTV / Flujo: usuario + contraseña + URL opcional (sin perfiles). */
export const usesLoginPanel = (row) => {
  if (!row) return false;
  if (isMailbox(row) || usesDeliveryCode(row) || usesExternalId(row) || usesSiteLoginSlots(row)) return false;
  if (row.loginPanel === true || row.loginPanel === 1 || row.product?.loginPanel === true || row.product?.loginPanel === 1) {
    return true;
  }
  if (row.presetId === "iptv") return true;
  const name = String(row.name || row.product?.name || "").toLowerCase();
  if (/sistema\s*(de\s*)?c[oó]digos/.test(name)) return false;
  return /iptv|flujo|lattv|xtream/.test(name);
};

export const fulfillmentOf = (row) => {
  const value = String(row?.fulfillmentType || row?.product?.fulfillmentType || "credentials");
  return FULFILLMENT_TYPES.includes(value) ? value : "credentials";
};

export const slotKindOf = (row) => {
  const value = String(row?.slotKind || row?.product?.slotKind || "pin_profile");
  return SLOT_KINDS.includes(value) ? value : "pin_profile";
};

export const needsAccountStock = (row) => !["code", "giftcard", "recharge", "license"].includes(fulfillmentOf(row));

export const needsSlots = (row) => slotKindOf(row) !== "none";

export const usesPinSlots = (row) => slotKindOf(row) === "pin_profile";

export const usesMemberEmail = (row) => fulfillmentOf(row) === "invite" || slotKindOf(row) === "email_member";

/** Sistema de Códigos / hosting madre: cada puesto = URL + usuario + contraseña. */
export const usesSiteLoginSlots = (row) => {
  if (!row) return false;
  if (slotKindOf(row) === "site_login") return true;
  if (isHostingSitesPreset(row.presetId) || isHostingSitesPreset(row.product?.presetId)) return true;
  const name = String(row.name || row.product?.name || "").toLowerCase();
  return /sistema\s*(de\s*)?c[oó]digos/.test(name);
};

export const usesDeliveryCode = (row) => ["code", "giftcard", "license", "panel"].includes(fulfillmentOf(row));

export const usesPinStock = (row) => ["code", "giftcard", "license"].includes(fulfillmentOf(row));

export const usesGeneratedCode = (row) => fulfillmentOf(row) === "panel";

export const usesExternalId = (row) => fulfillmentOf(row) === "recharge";

export const isMailbox = (row) => fulfillmentOf(row) === "mailbox";

export const hasNoValidity = (row) => isMailbox(row) || fulfillmentOf(row) === "giftcard";

export const originCountryOf = (row) =>
  String(row?.originCountry || row?.product?.originCountry || "").toUpperCase();

export const countryNameOf = (code) => {
  const found = COUNTRY_OPTIONS.find((row) => row.code === String(code || "").toUpperCase());
  return found ? found.name : String(code || "");
};

export const deviceLimitOf = (row) => {
  const raw = row?.deviceLimit != null && row?.deviceLimit !== "" ? row.deviceLimit : row?.product?.deviceLimit;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
};

export const warrantyDaysOf = (row) => {
  const raw = row?.warrantyDays != null && row?.warrantyDays !== "" ? row.warrantyDays : row?.product?.warrantyDays;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
};

export const lotSizeOf = (row) => {
  const raw = row?.lotSize != null && row?.lotSize !== "" ? row.lotSize : row?.product?.lotSize;
  const value = Number(raw);
  return Number.isFinite(value) && value > 1 ? Math.floor(value) : 1;
};

const productNameOf = (row) =>
  `${row?.name || row?.product?.name || ""} ${row?.planLabel || row?.product?.planLabel || ""}`.toLowerCase();

/**
 * Cómo se llama la unidad que se vende: perfil, miembro, dispositivo, usuario o panel.
 * Manda el modo de configuración; si el producto quedó como «perfil con PIN» pero no es
 * streaming (CapCut, ChatGPT, IPTV…), se reconoce por el nombre. Igual que el backend (negocioCatalog.unitNoun).
 */
export const unitNoun = (row) => {
  const kind = slotKindOf(row);
  const type = fulfillmentOf(row);
  const name = productNameOf(row);
  if (kind === "device" || type === "device") return "device";
  if (kind === "email_member" || type === "invite") return "member";
  if (kind === "site_login" || isHostingSitesPreset(row?.presetId) || /sistema\s*(de\s*)?c[oó]digos|hosting/.test(name)) return "panel";
  if (row?.loginPanel === true || row?.loginPanel === 1 || row?.product?.loginPanel === true || row?.product?.loginPanel === 1 || row?.presetId === "iptv" || /iptv|flujo|lattv|xtream/.test(name)) return "user";
  if (/cap\s*cut/.test(name)) return "device";
  if (/chat\s*gpt|canva|spotify|youtube/.test(name)) return "member";
  return "profile";
};

export const slotNoun = (row) => (slotKindOf(row) === "none" && unitNoun(row) === "profile" ? "unit" : unitNoun(row));

/** Nombres automáticos «Perfil 3» se muestran con el nombre de la modalidad («Dispositivo 3»). */
export const unitSlotName = (row, name, t) => {
  const value = String(name ?? "").trim();
  const match = value.match(/^perfil\s*(\d{1,4})$/i);
  if (!match || unitNoun(row) === "profile") return value;
  return `${t ? t(`pages.catalog.slots.${unitNoun(row)}.one`) : unitNoun(row)} ${match[1]}`;
};

export const slotLabel = (row, t, plural = false) =>
  t(`pages.catalog.slots.${slotNoun(row)}.${plural ? "plural" : "one"}`);

export const slotMaxLabel = (row, t) => t(`pages.catalog.slots.${slotNoun(row)}.max`);

export const saleTypeKey = (row) => unitNoun(row);

export const productTitle = (product) => {
  if (!product) return "";
  return product.planLabel ? `${product.name} · ${product.planLabel}` : product.name;
};

export const isCompleteAccountOffer = (row) =>
  (fulfillmentOf(row) === "credentials" && slotKindOf(row) === "none") ||
  (!!row && row.sellFullAccount && !row.sellByProfile);

export const productTypeKey = (row) => {
  if (!row) return "profile";
  if (isMailbox(row)) return "mailbox";
  if (fulfillmentOf(row) === "invite") return "domain";
  if (fulfillmentOf(row) === "device" || slotKindOf(row) === "device") return "device";
  if (["code", "giftcard", "license", "panel"].includes(fulfillmentOf(row))) return "code";
  if (fulfillmentOf(row) === "recharge") return "recharge";
  if (isCompleteAccountOffer(row)) return usesLoginPanel(row) ? "user" : "full";
  return unitNoun(row);
};

export const productTypeLabel = (row, t) => fulfillmentOf(row) === "giftcard" ? "Gift card" : t(`pages.catalog.offerTypes.${productTypeKey(row)}`);

/** Etiqueta de producto en selects: nombre · tipo · plan · [Nd] · [N disp.]
 * Los días salen de NegocioProducts.durationDays (catálogo; por defecto 30).
 * El stock (disp.) son unidades vendibles: puestos libres si vende por perfil,
 * o cuentas available (sin vencidas/falla) si es cuenta completa. */
export const productOfferLabel = (product, t, { days = true, stock = true } = {}) => {
  if (!product) return "";
  const parts = [product.name, productTypeLabel(product, t)];
  if (product.planLabel) parts.push(product.planLabel);
  if (days && product.durationDays) parts.push(`${product.durationDays}d`);
  if (stock) {
    const n = Number(
      product.availableStock != null && product.availableStock !== ""
        ? product.availableStock
        : product.availableAccounts != null
          ? product.availableAccounts
          : NaN
    );
    if (Number.isFinite(n)) {
      const label =
        typeof t === "function"
          ? String(t("pages.products.availableShort") || "{n} disp.").replace("{n}", String(n))
          : `${n} disp.`;
      parts.push(label);
    }
  }
  return parts.filter(Boolean).join(" · ");
};

/** Etiqueta única para selects/filtros (plan + tipo + stock). El precio es de la cuenta, no del producto. */
export const productFilterLabel = (product, t, _formatMoney) => {
  if (!product) return "";
  return productOfferLabel(product, t);
};

export const productSiteUrl = (product) => {
  const raw = String(product?.serviceUrl || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
};

export const DEFAULT_PLAN_CATALOG = [
  "Standard",
  "Premium",
  "Familiar",
  "EDU",
  "1 pantalla",
  "2 pantallas",
  "Completa",
  "PRO",
];

export const parsePlanCatalog = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  const raw = String(value || "").trim();
  if (!raw) return [...DEFAULT_PLAN_CATALOG];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsePlanCatalog(parsed);
  } catch (e) {
    /* plain text */
  }
  const listed = raw.split(/\n|,/).map((item) => item.trim()).filter(Boolean);
  return listed.length ? listed : [...DEFAULT_PLAN_CATALOG];
};

export const isDomainSale = (row) => fulfillmentOf(row) === "invite" || slotKindOf(row) === "email_member";

export const isRentalSale = (row) => fulfillmentOf(row) === "credentials" && slotKindOf(row) !== "none";

export const isFullAccountSale = (row) => fulfillmentOf(row) === "credentials" && slotKindOf(row) === "none";

export const showsOwnershipPick = (row) => {
  if (usesSiteLoginSlots(row) || slotKindOf(row) === "site_login") return false;
  const type = fulfillmentOf(row);
  return type === "credentials" || type === "invite";
};

export const presetIdOf = (row) => {
  const type = fulfillmentOf(row);
  const slot = slotKindOf(row);
  if (type === "invite" && Number(row?.maxProfiles) === 2) return "canvaEdu";
  if (type === "invite") return "family";
  if (type === "device" || slot === "device") return "capcut";
  if (type === "panel") return "panel";
  if (type === "giftcard") return "giftcard";
  if (type === "code" || type === "license") return "code";
  if (type === "recharge") return "recharge";
  if (type === "mailbox") return "mailbox";
  if (type === "credentials" && slot === "site_login") return "hostingSites";
  if (type === "credentials" && slot === "none") {
    if (row?.loginPanel || row?.product?.loginPanel) return "iptv";
    const name = String(row?.name || row?.product?.name || "").toLowerCase();
    if (/iptv|flujo|lattv|xtream/.test(name)) return "iptv";
    return "fullAccount";
  }
  const name = String(row?.name || row?.product?.name || "").toLowerCase();
  if (/sistema\s*(de\s*)?c[oó]digos/.test(name)) return "hostingSites";
  return "streaming";
};

export const applyPreset = (presetId, current = {}) => {
  const preset = PRODUCT_PRESETS.find((row) => row.id === presetId);
  if (!preset) return { ...current, presetId: presetId || "" };
  const { id, ...rest } = preset;
  const defaults = applyFulfillmentDefaults(preset.fulfillmentType);
  return {
    ...current,
    ...defaults,
    ...rest,
    presetId: id,
    name: current.name,
    description: current.description,
    originCountry: current.originCountry,
    warrantyDays: current.warrantyDays,
    parentProductId: current.parentProductId,
    planLabel: current.planLabel,
    isActive: current.isActive,
    isNew: current.isNew,
    id: current.id,
    imageUrl: current.imageUrl,
  };
};

export const applyOwnership = (kind, current = {}) => {
  if (kind === "domain") {
    const defaults = applyFulfillmentDefaults("invite");
    const presetId = Number(current.maxProfiles) === 2 ? "canvaEdu" : "family";
    return {
      ...current,
      ...defaults,
      maxProfiles: current.maxProfiles || defaults.maxProfiles,
      presetId: current.presetId === "canvaEdu" ? "canvaEdu" : presetId,
    };
  }
  if (kind === "full") {
    const defaults = applyFulfillmentDefaults("credentials");
    return {
      ...current,
      ...defaults,
      fulfillmentType: "credentials",
      slotKind: "none",
      maxProfiles: 1,
      sellByProfile: false,
      sellFullAccount: true,
      pinEnabled: false,
      presetId: "fullAccount",
    };
  }
  if (kind === "rental") {
    const defaults = applyFulfillmentDefaults("credentials");
    return {
      ...current,
      ...defaults,
      maxProfiles: current.maxProfiles || defaults.maxProfiles,
      sellByProfile: true,
      sellFullAccount: true,
      pinEnabled: true,
      presetId: "streaming",
    };
  }
  return current;
};

export const parseEmailLot = (text, fallbackPassword = "") =>
  String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(/[\t|,;]+/).map((part) => part.trim()).filter(Boolean);
      return { email: parts[0] || "", password: parts[1] || fallbackPassword || "" };
    })
    .filter((unit) => unit.email);

export const slotCapacity = (row) => {
  if (slotKindOf(row) === "none") return 0;
  const device = deviceLimitOf(row);
  if (slotKindOf(row) === "device" && device) return device;
  const max = Number(row?.maxProfiles || row?.product?.maxProfiles || row?.slotCount || 0);
  return max > 0 ? max : device || 0;
};

export const productFamilyIds = (products, product) => {
  if (!product) return [];
  const parent = Number(product.parentProductId || product.id);
  if (!parent) return product.id ? [Number(product.id)] : [];
  return (products || [])
    .filter((p) => Number(p.id) === parent || Number(p.parentProductId) === parent)
    .map((p) => Number(p.id));
};

export const DURATION_PLAN_PRESETS = [
  { days: 30, key: "month" },
  { days: 90, key: "quarter" },
  { days: 365, key: "year" },
];

export const sellableProfiles = (account, keepId) => {
  const profiles = [...(account?.profiles || [])].sort((a, b) => (a.slotNumber || 0) - (b.slotNumber || 0));
  const cap = slotCapacity(account);
  const assigned = profiles.filter((p) => p.status === "assigned").length;
  const keep = keepId != null ? Number(keepId) : null;
  const free = profiles.filter(
    (p) => (p.status === "available" && !p.contactId) || (keep != null && Number(p.id) === keep)
  );
  if (!cap) return free;
  const remaining = Math.max(0, cap - assigned);
  const kept = keep != null ? free.filter((p) => Number(p.id) === keep) : [];
  const extras = free.filter((p) => keep == null || Number(p.id) !== keep).slice(0, remaining);
  return [...kept, ...extras];
};

export const applyFulfillmentDefaults = (fulfillmentType) => {
  const type = FULFILLMENT_TYPES.includes(fulfillmentType) ? fulfillmentType : "credentials";
  if (type === "invite") {
    return { fulfillmentType: type, slotKind: "email_member", maxProfiles: 5, sellByProfile: true, sellFullAccount: false, pinEnabled: false, lotSize: 1 };
  }
  if (type === "device") {
    return { fulfillmentType: type, slotKind: "device", maxProfiles: 3, deviceLimit: 3, sellByProfile: true, sellFullAccount: true, pinEnabled: false, lotSize: 1 };
  }
  if (type === "mailbox") {
    return { fulfillmentType: type, slotKind: "none", maxProfiles: 1, sellByProfile: false, sellFullAccount: true, pinEnabled: false, lotSize: 1 };
  }
  if (type === "panel") {
    return { fulfillmentType: type, slotKind: "none", maxProfiles: 10, sellByProfile: true, sellFullAccount: false, pinEnabled: false, lotSize: 1 };
  }
  if (type === "code" || type === "recharge" || type === "license") {
    return { fulfillmentType: type, slotKind: "none", maxProfiles: 1, sellByProfile: false, sellFullAccount: true, pinEnabled: false, lotSize: 1 };
  }
  return { fulfillmentType: "credentials", slotKind: "pin_profile", maxProfiles: 5, sellByProfile: true, sellFullAccount: true, lotSize: 1 };
};

export const COUNTRY_OPTIONS = [
  { code: "CO", name: "Colombia" },
  { code: "PE", name: "Perú" },
  { code: "EC", name: "Ecuador" },
  { code: "MX", name: "México" },
  { code: "AR", name: "Argentina" },
  { code: "CL", name: "Chile" },
  { code: "BR", name: "Brasil" },
  { code: "US", name: "Estados Unidos" },
  { code: "ES", name: "España" },
  { code: "VE", name: "Venezuela" },
  { code: "BO", name: "Bolivia" },
  { code: "PY", name: "Paraguay" },
  { code: "UY", name: "Uruguay" },
  { code: "PA", name: "Panamá" },
  { code: "CR", name: "Costa Rica" },
  { code: "GT", name: "Guatemala" },
  { code: "HN", name: "Honduras" },
  { code: "SV", name: "El Salvador" },
  { code: "NI", name: "Nicaragua" },
  { code: "DO", name: "República Dominicana" },
  { code: "PR", name: "Puerto Rico" },
  { code: "PT", name: "Portugal" },
];
